import { useNavigate, useParams } from "react-router-dom";
import { ApiOperationItems } from "~enums/apiOperationItems";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import type { CreateLeaderCriteriaModel, UpdateLeaderCriteriaModel } from "~pages/LeaderCriteria/types/leaderCriteriaModel.ts";
import { useCreateLeaderCriteria } from "./useCreateLeaderCriteria.ts";
import { useDeleteLeaderCriteria } from "./useDeleteLeaderCriteria.ts";
import { useUpdateLeaderCriteria } from "./useUpdateLeaderCriteria.ts";

export const useLeaderCriteriaService = () => {
  const { mutateAsync: createLeaderCriteriaFn, isPending: isCreatePending } = useCreateLeaderCriteria();
  const { mutateAsync: updateLeaderCriteriaFn, isPending: isUpdatePending } = useUpdateLeaderCriteria();
  const { mutateAsync: deleteLeaderCriteriaFn, isPending: isDeletePending } = useDeleteLeaderCriteria();
  const { customerId, sport, organizationId } = useParams();
  const navigate = useNavigate();

  const { create, update, remove } = useApiOperations(ApiOperationItems.LEADER_CRITERIA);

  const listUrl = `/${customerId}/admin/${sport}/${organizationId}/leaders-qualification`;

  const createLeaderCriteria = async (payload: CreateLeaderCriteriaModel) => {
    const result = await create(() => createLeaderCriteriaFn(payload), {
      successMessageId: "leaders.qualification.create.success",
    });

    if (result) {
      navigate(listUrl, { state: { search: payload.name } });
    }

    return result;
  };

  const updateLeaderCriteria = (leaderCriteriaId: string, payload: Omit<UpdateLeaderCriteriaModel, "leaderCriteriaId">) =>
    update(() => updateLeaderCriteriaFn({ ...payload, leaderCriteriaId }), {
      successMessageId: "leaders.qualification.update.success",
    });

  const deleteLeaderCriteria = async (leaderCriteriaId: string) =>
    Boolean(
      await remove(() => deleteLeaderCriteriaFn(leaderCriteriaId), {
        successMessageId: "leaders.qualification.delete.success",
      }),
    );

  return {
    createLeaderCriteria,
    isCreatePending,
    updateLeaderCriteria,
    isUpdatePending,
    deleteLeaderCriteria,
    isDeletePending,
  };
};
